/**
 * Reflow a stored agreement body for on-screen display.
 *
 * The agreement templates in `backend/services/agreement_templates.py` are
 * hard-wrapped at ~68 characters so they read cleanly in a code editor
 * AND on a printed A4 PDF. On a narrow mobile viewport those hard newlines
 * land mid-sentence and produce ugly, apparently-random line breaks:
 *
 *   3.2  The Venue commits to honour every valid Allure
 *   pass scanned at its
 *       premises during opening hours and to apply the
 *   published discount
 *       fairly to redeemed bills.
 *
 * We do NOT touch the stored `body_snapshot` — it must stay byte-identical
 * to what the user accepted (legal integrity). This helper is applied at
 * render time only: continuation lines within a clause are joined into a
 * single logical line so the browser can wrap them naturally at the actual
 * container width. Blank lines between paragraphs are preserved.
 *
 * A "logical line start" is any of:
 *   * clause like `1.1`, `10.2`, `12.3.1`
 *   * section header like `1. NATURE OF THE AGREEMENT`
 *   * all-caps heading (e.g. `ACCEPTANCE`, `LANGUAGE NOTICE / ΓΛΩΣΣΙΚΗ ...`)
 *   * indented sub-item like `      (a) `, `      (ii) ` (indent required
 *     to avoid false-positives on `(i)` / `(ii)` occurring in prose)
 *   * a `Label: value` row (contact block)
 *   * a rule line of ─ characters (the divider under the bilingual notice)
 *   * the first non-blank line AFTER an all-caps or section header
 *     (headings are always terminal — whatever follows starts fresh)
 */
export function reflowAgreementBody(body) {
  if (!body || typeof body !== 'string') return '';
  const lines = body.split('\n');
  const output = [];
  let buffer = '';
  let bufferIsHeader = false;

  const flush = () => {
    if (buffer) {
      output.push(buffer);
      buffer = '';
    }
  };

  const CLAUSE_RE = /^(\d+\.\d+(\.\d+)?)(\s|$)/;
  const SECTION_RE = /^\d+\.\s+[A-ZΑ-Ω]/;
  // Contact-block labels are short (≤40 chars) and never contain sentence
  // punctuation (commas, periods) before the colon — this prevents prose
  // like "…marketplace fee is ECONOMICALLY BORNE BY THE GUEST, not by the
  // Venue: the guest pays…" from being mis-detected as a label row.
  const LABEL_RE = /^[A-ZΑ-Ωa-zα-ω][^:,.]{2,40}:\s/;
  const RULE_RE = /^[─—\-=]{5,}$/;
  const SUB_ITEM_RE = /^\([a-zα-ω]+\)(\s|$)/;

  for (const raw of lines) {
    if (!raw.trim()) {
      flush();
      output.push('');
      bufferIsHeader = false;
      continue;
    }
    const trimmed = raw.trim();
    const isAllCaps =
      trimmed === trimmed.toUpperCase() &&
      /[A-ZΑ-Ω]/.test(trimmed) &&
      trimmed.length > 3 &&
      trimmed.length < 90;
    const isSection = SECTION_RE.test(trimmed);
    const isClause = CLAUSE_RE.test(trimmed);
    const isLabel = LABEL_RE.test(trimmed);
    const isRule = RULE_RE.test(trimmed);
    // Sub-items must be indented in the source so mid-sentence roman
    // numerals like "(ii)" don't false-trigger.
    const rawIsIndented = raw.startsWith(' ') || raw.startsWith('\t');
    const isSubItem = rawIsIndented && SUB_ITEM_RE.test(trimmed);

    const isHeaderLine = isAllCaps || isSection;
    const startsNewLine =
      isHeaderLine ||
      isClause ||
      isSubItem ||
      isLabel ||
      isRule ||
      bufferIsHeader; // headings are terminal — force fresh line after

    if (startsNewLine) {
      flush();
      buffer = trimmed;
      bufferIsHeader = isHeaderLine;
    } else {
      buffer = buffer ? `${buffer} ${trimmed}` : trimmed;
      bufferIsHeader = false;
    }
  }
  flush();
  return output.join('\n');
}

export default reflowAgreementBody;
